import { fetchWithChatFeedback } from './chatFeedbackRequired';
/**
 * Wage Determination API Functions
 * Handles document upload, extraction, SAM.gov search, and PDF export
 */

import {
  extractDetailMessage,
  normalizeApiError,
  toUserFacingError,
  USER_FACING_ERROR_MESSAGES,
} from '../utils/userFacingError';

const API_BASE_URL = process.env.REACT_APP_API_URL || '';

// Types
export interface LocationInfo {
  address?: string;
  city?: string;
  state?: string;
  state_abbrev?: string;
  county?: string;
}

export type ConstructionType = 'Building' | 'Residential' | 'Highway' | 'Heavy';

/**
 * Optional provenance for an acquisition reference date extracted from a SOW
 * or supplied by a user. All fields are additive so pre-feature flow data
 * remains valid.
 */
export interface AcquisitionDateInfo {
  date: string;
  kind?: 'acquisition' | 'bid_opening' | 'award' | 'option' | string;
  source?: 'sow' | 'manual' | string;
  evidence_text?: string;
}

export interface JobClassification {
  title: string;
  rate: number;
  fringes: number;
  total: number;
}

export interface WageDetermination {
  wd_number: string;
  construction_type: ConstructionType;
  geographic_scope: string;
  modification_date: string;
  classifications: JobClassification[];
  notes?: Record<string, string[]>;
  full_text?: string;
  sam_url?: string;
  status?: string;
  publication_date?: string;
  effective_date?: string;
  modification_number?: string;
  supersedes_decision_id?: string;
  /** Additive SAM.gov history provenance; absent from legacy/current results. */
  decision_id?: string;
  source_method?: string;
  selection_reason?: string;
  historical?: boolean;
}

export interface WDExtractionResult {
  location: LocationInfo;
  construction_type: ConstructionType;
  acquisition_date_info?: AcquisitionDateInfo;
  /**
   * SOW dates that were individually valid but ambiguous as a group.  They
   * are informational only until the user deliberately chooses or enters one.
   */
  acquisition_date_candidates?: AcquisitionDateInfo[];
  acquisition_date_requires_user_selection?: boolean;
}

export interface WDSearchParams {
  state: string;
  county: string;
  construction_type: ConstructionType;
  acquisition_date?: string;
}

export interface WDSearchResult {
  wage_determinations: WageDetermination[];
  search_params: WDSearchParams;
  acquisition_date?: string;
  acquisition_date_info?: AcquisitionDateInfo;
  review_required?: boolean;
  /**
   * Actionable, user-safe guidance from the backend when a historical result
   * needs a human decision. Omitted for legacy and unambiguous results.
   */
  review_message?: string;
  /** Present only for an acquisition-date (historical) search. */
  historical_search_status?: 'matches' | 'no_match';
  /** User-safe explanation for an otherwise empty historical search. */
  historical_search_message?: string;
  /**
   * Optional, source-validated research context for an explicitly requested
   * fiscal-year update search. It is separate from the primary WD result.
   */
  fiscal_year_updates?: FiscalYearUpdates;
}

export interface FiscalYearUpdate {
  decision_id: string;
  revision_or_modification: string;
  publication_date: string;
  effective_date?: string;
  update_type?: 'Annual reissue' | 'Modification' | 'Update' | string;
  status?: string;
  coverage?: string;
  source_url: string;
  source_method?: string;
  /**
   * Optional card-ready WD fields. These are additive because older saved
   * searches only contain source metadata, while newer responses may provide
   * the same detail used by the selected WD card.
   */
  wd_number?: string;
  construction_type?: ConstructionType;
  geographic_scope?: string;
  modification_date?: string;
  modification_number?: string;
  classifications?: JobClassification[];
  notes?: Record<string, string[]>;
  full_text?: string;
  sam_url?: string;
}

export interface FiscalYearUpdates {
  fiscal_year: number;
  window_start: string;
  window_end: string;
  source_date_basis: 'publication_date' | 'documented_source_date' | string;
  /** The matching WD already in effect on the first day of the federal FY. */
  baseline?: FiscalYearUpdate;
  updates: FiscalYearUpdate[];
  omitted_count?: number;
  omission_reason?: string;
}

export interface WDSearchRequest extends WDSearchParams {
  state_abbrev: string;
  acquisition_date_info?: AcquisitionDateInfo;
}

export interface WDUploadResponse {
  success: boolean;
  file_path?: string;
  artifact_id?: string;
  file_size: number;
  file_type: string;
}

export interface WDDocumentInfo {
  filename: string;
  file_type: string;
  size: number;
  modified_at: string;
}

/**
 * Get user email from localStorage for authentication headers
 */
function getUserEmail(): string {
  return localStorage.getItem('userEmail') || (window as any).userEmail || '';
}

/**
 * Upload a document for wage determination extraction
 * @param flowId - The flow/session identifier
 * @param file - The file to upload
 * @returns Upload response with file path and size
 */
export async function uploadWageDetDocument(
  flowId: string,
  file: File
): Promise<WDUploadResponse> {
  const formData = new FormData();
  formData.append('file', file);
  formData.append('flow_id', flowId);

  const headers: Record<string, string> = {};

  const response = await fetchWithChatFeedback(`${API_BASE_URL}/api/flows/${flowId}/wage-det/upload`, {
    method: 'POST',
    headers,
    body: formData,
    credentials: 'include',
  });

  if (!response.ok) {
    const error = await response.json().catch(() => ({ detail: 'Upload failed' }));
    const detail = extractDetailMessage(error.detail, `Upload failed with status ${response.status}`);
    throw new Error(detail);
  }

  return response.json();
}

/**
 * Get uploaded document info for a flow
 * @param flowId - The flow/session identifier
 * @returns Document info if exists
 */
export async function getWageDetDocument(flowId: string): Promise<WDDocumentInfo | null> {
  const headers: Record<string, string> = {
    'Content-Type': 'application/json',
  };

  const response = await fetchWithChatFeedback(`${API_BASE_URL}/api/flows/${flowId}/wage-det/document`, {
    method: 'GET',
    headers,
    credentials: 'include',
  });

  if (!response.ok) {
    if (response.status === 404) {
      return null;
    }
    const error = await response.json().catch(() => ({ detail: 'Failed to get document' }));
    throw new Error(extractDetailMessage(error.detail, 'Failed to get document'));
  }

  return response.json();
}

/**
 * Delete uploaded document for a flow
 * @param flowId - The flow/session identifier
 */
export async function deleteWageDetDocument(flowId: string): Promise<{ success: boolean }> {
  const headers: Record<string, string> = {};

  const response = await fetchWithChatFeedback(`${API_BASE_URL}/api/flows/${flowId}/wage-det/document`, {
    method: 'DELETE',
    headers,
    credentials: 'include',
  });

  if (!response.ok) {
    const error = await response.json().catch(() => ({ detail: 'Delete failed' }));
    throw new Error(extractDetailMessage(error.detail, 'Delete failed'));
  }

  return response.json();
}

/**
 * Run extraction agent on uploaded document
 * @param flowId - The flow/session identifier
 * @returns Extracted location and construction type
 */
export async function extractWageDetInfo(flowId: string, forceRefresh: boolean = false): Promise<WDExtractionResult> {
  const headers: Record<string, string> = {
    'Content-Type': 'application/json',
  };

  const url = forceRefresh
    ? `${API_BASE_URL}/api/flows/${flowId}/wage-det/extract?force_refresh=true`
    : `${API_BASE_URL}/api/flows/${flowId}/wage-det/extract`;

  const response = await fetchWithChatFeedback(url, {
    method: 'POST',
    headers,
    credentials: 'include',
  });

  if (!response.ok) {
    const error = await response.json().catch(() => ({ detail: 'Extraction failed' }));
    throw new Error(extractDetailMessage(error.detail, 'Extraction failed'));
  }

  return response.json();
}

/** Read a cache response without masking authentication or storage failures. */
async function readWageCacheResponse<T>(response: Response): Promise<T | null> {
  if (response.status === 401) {
    window.dispatchEvent(new Event('auth-expired'));
    throw Object.assign(new Error(USER_FACING_ERROR_MESSAGES.sign_in_again), {
      error_code: 'sign_in_again',
    });
  }
  if (!response.ok) {
    const body = await response.json().catch(() => undefined);
    const normalized = normalizeApiError(body);
    const error = Object.assign(new Error(normalized.message), {
      error_code: normalized.errorCode,
      technical_detail: normalized.technicalDetail,
    });
    error.message = toUserFacingError(error);
    throw error;
  }
  return response.json();
}

/**
 * Get cached extraction result
 * @param flowId - The flow/session identifier
 * @returns Cached extraction result or null
 */
export async function getCachedExtraction(flowId: string): Promise<WDExtractionResult | null> {
  const headers: Record<string, string> = {};

  const response = await fetchWithChatFeedback(`${API_BASE_URL}/api/flows/${flowId}/wage-det/extraction`, {
    method: 'GET',
    headers,
    credentials: 'include',
  });

  return readWageCacheResponse<WDExtractionResult>(response);
}

/**
 * Save extraction data (user edits)
 * @param flowId - The flow/session identifier
 * @param data - Extraction data to save
 */
export async function saveExtractionData(
  flowId: string,
  data: WDExtractionResult
): Promise<{ success: boolean }> {
  const headers: Record<string, string> = {
    'Content-Type': 'application/json',
  };

  const response = await fetchWithChatFeedback(`${API_BASE_URL}/api/flows/${flowId}/wage-det/extraction`, {
    method: 'PUT',
    headers,
    body: JSON.stringify(data),
    credentials: 'include',
  });

  if (!response.ok) {
    const error = await response.json().catch(() => ({ detail: 'Save failed' }));
    throw new Error(extractDetailMessage(error.detail, 'Save failed'));
  }

  return response.json();
}

/**
 * Search SAM.gov for wage determinations
 * @param flowId - The flow/session identifier
 * @param params - Search parameters
 * @returns List of wage determinations
 */
export async function searchWageDeterminations(
  flowId: string,
  params: WDSearchRequest,
  forceRefresh?: boolean
): Promise<WDSearchResult> {
  const headers: Record<string, string> = {
    'Content-Type': 'application/json',
  };

  const response = await fetchWithChatFeedback(`${API_BASE_URL}/api/flows/${flowId}/wage-det/search`, {
    method: 'POST',
    headers,
    body: JSON.stringify({ ...params, force_refresh: forceRefresh || false }),
    credentials: 'include',
  });

  if (!response.ok) {
    const error = await response.json().catch(() => ({ detail: 'Search failed' }));
    throw new Error(extractDetailMessage(error.detail, 'Search failed'));
  }

  return response.json();
}

/**
 * Get cached search results
 * @param flowId - The flow/session identifier
 * @returns Cached search results or null
 */
export async function getCachedSearchResults(flowId: string): Promise<WDSearchResult | null> {
  const headers: Record<string, string> = {};

  const response = await fetchWithChatFeedback(`${API_BASE_URL}/api/flows/${flowId}/wage-det/search-results`, {
    method: 'GET',
    headers,
    credentials: 'include',
  });

  return readWageCacheResponse<WDSearchResult>(response);
}

/**
 * Save selected wage determinations
 * @param flowId - The flow/session identifier
 * @param selectedWDs - Selected wage determination numbers
 */
export async function saveSelectedWDs(
  flowId: string,
  selectedWDs: string[]
): Promise<{ success: boolean }> {
  const headers: Record<string, string> = {
    'Content-Type': 'application/json',
  };

  const response = await fetchWithChatFeedback(`${API_BASE_URL}/api/flows/${flowId}/wage-det/select`, {
    method: 'POST',
    headers,
    body: JSON.stringify({ selected_wd_numbers: selectedWDs }),
    credentials: 'include',
  });

  if (!response.ok) {
    const error = await response.json().catch(() => ({ detail: 'Save selection failed' }));
    throw new Error(extractDetailMessage(error.detail, 'Save selection failed'));
  }

  return response.json();
}

/**
 * Get selected wage determinations
 * @param flowId - The flow/session identifier
 * @returns List of selected WD numbers
 */
export async function getSelectedWDs(flowId: string): Promise<string[]> {
  const headers: Record<string, string> = {};

  const response = await fetchWithChatFeedback(`${API_BASE_URL}/api/flows/${flowId}/wage-det/selected`, {
    method: 'GET',
    headers,
    credentials: 'include',
  });

  if (!response.ok) {
    if (response.status === 404) {
      return [];
    }
    return [];
  }

  const data = await response.json();
  return data.selected_wd_numbers || [];
}

// COMMENTED OUT [wage-det-refactor]
// /**
//  * Generate PDF report for selected wage determinations
//  * @param flowId - The flow/session identifier
//  * @returns PDF blob
//  */
// export async function generateWageDetPDF(flowId: string): Promise<Blob> {
//   const headers: Record<string, string> = {};
//
//   const response = await fetch(`${API_BASE_URL}/api/flows/${flowId}/wage-det/export`, {
//     method: 'GET',
//     headers,
//     credentials: 'include',
//   });
//
//   if (!response.ok) {
//     const error = await response.json().catch(() => ({ detail: 'PDF generation failed' }));
//     throw new Error(error.detail || 'PDF generation failed');
//   }
//
//   return response.blob();
// }
// COMMENTED OUT [wage-det-refactor]

/**
 * Download wage determination as TXT file
 * @param flowId - The flow/session identifier
 * @param wdNumber - The wage determination number
 * @returns TXT blob
 */
export async function downloadWageDetTxt(flowId: string, wdNumber: string): Promise<Blob> {
  const response = await fetchWithChatFeedback(`${API_BASE_URL}/api/flows/${flowId}/wage-det/${wdNumber}/download-txt`, {
    credentials: 'include',
  });
  if (!response.ok) throw new Error(`Failed to download TXT: ${response.statusText}`);
  return response.blob();
}

/**
 * Geocode a free-text location (city / ZIP / address) to its county & state.
 * Stateless lookup — not flow-scoped. Returns the resolved LocationInfo
 * (`{ county, state, state_abbrev }`) or null when nothing resolves / on error.
 * @param query - Free-text location (city, ZIP, or full address)
 * @param stateAbbrev - Optional currently-selected state to disambiguate
 * @returns Resolved location info, or null on a miss/error
 */
export async function geocodeLocation(
  query: string,
  stateAbbrev?: string
): Promise<LocationInfo | null> {
  try {
    return await resolveCountyLocation(query, stateAbbrev);
  } catch (err) {
    console.error('[wageDetFunctions] geocodeLocation failed:', err);
    return null;
  }
}

/**
 * Shared, resolver-backed county lookup for construction location pickers.
 * Unlike the legacy convenience wrapper above, this preserves a transport
 * failure so a caller can distinguish it from a normal no-match.
 */
export async function resolveCountyLocation(
  query: string,
  stateAbbrev?: string,
): Promise<LocationInfo | null> {
  const headers: Record<string, string> = {
    'Content-Type': 'application/json',
  };
  const response = await fetchWithChatFeedback(`${API_BASE_URL}/api/wage-det/geocode`, {
    method: 'POST', headers,
    body: JSON.stringify({ query, state_abbrev: stateAbbrev }),
    credentials: 'include',
  });
  if (response.status === 401) {
    window.dispatchEvent(new Event('auth-expired'));
    throw Object.assign(new Error(USER_FACING_ERROR_MESSAGES.sign_in_again), {
      error_code: 'sign_in_again',
    });
  }
  const result = await response.json().catch(() => {
    throw new Error(USER_FACING_ERROR_MESSAGES.unrecognized);
  });
  if (!response.ok || result?.success !== true) {
    throw new Error(result?.error || 'Location search failed');
  }
  const location = result.data;
  if (location === null) return null;
  if (!location || typeof location !== 'object' || Array.isArray(location)
      || typeof location.county !== 'string' || !location.county.trim()) {
    throw new Error(USER_FACING_ERROR_MESSAGES.unrecognized);
  }
  return location;
}

/**
 * Get flow stage data for wage determination workflow
 * @param flowId - The flow/session identifier
 * @returns Current stage number
 */
export async function getWageDetStage(flowId: string): Promise<number> {
  const headers: Record<string, string> = {};

  const response = await fetchWithChatFeedback(`${API_BASE_URL}/api/flows/${flowId}`, {
    method: 'GET',
    headers,
    credentials: 'include',
  });

  if (!response.ok) {
    return 0;
  }

  const data = await response.json();
  return data.metadata?.currentStage || 0;
}
